import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';

import { getEventDashboard } from '../../../axios/Eventlog/EventLogApi';
import { useLoading } from '../../../context/LoadingContext';
import { ControlpanelSuccess3thButton } from '../../../components/StyledButton';

ChartJS.register(ArcElement, Tooltip, Legend);

const getListLength = (list) =>
   Array.isArray(list) ? list.length : 0;

const parseDashboardPayload = (raw) => {
   if (raw == null) return null;

   if (typeof raw === 'string') {
      try {
         return JSON.parse(raw);
      } catch {
         return null;
      }
   }

   return raw;
};

const EmployeesChart = (props) => {
   const {
      refreshData,
      setDialogTitle,
      setDialogListEmployee,
      setOpenShowInfoDialog,
      setDialogStatusType,
   } = props;

   const [show, hide] = useLoading();
   const { t } = useTranslation();
   const navigate = useNavigate();
   const requestIdRef = useRef(0);

   const [employeeData, setEmployeeData] = useState({});
   const [checkInCount, setCheckInCount] = useState(0);
   const [checkOutCount, setCheckOutCount] = useState(0);
   const [onLeaveCount, setOnLeaveCount] = useState(0);

   const totalUser =
      checkInCount +
      checkOutCount +
      onLeaveCount;

   const [chartData, setChartData] = useState({
      labels: [
         t('common.in'),
         t('common.out'),
         t('common.on-leave'),
      ],
      datasets: [
         {
            data: [0, 0, 0],
            backgroundColor: [
               '#009900',
               '#1557F5',
               '#FF006E',
            ],
            borderWidth: 0,
            spacing: 8,
            borderRadius: 12,
         },
      ],
   });

   const centerTextPlugin = useMemo(
      () => ({
         id: 'employeesCenterText',

         afterDraw(chart) {
            const { ctx } = chart;
            const meta = chart.getDatasetMeta(0);

            if (!meta?.data?.length) return;

            const x = meta.data[0].x;
            const y = meta.data[0].y;

            const config =
               chart.options.plugins?.employeesCenterText || {};

            const total = config.total ?? 0;
            const title = config.title ?? '';

            ctx.save();

            ctx.textAlign = 'center';

            ctx.fillStyle = '#1F2937';
            ctx.font = '500 14px Montserrat';

            ctx.fillText(
               title,
               x,
               y - 20
            );

            ctx.fillStyle = '#1F2937';
            ctx.font = '700 32px Montserrat';

            ctx.fillText(
               String(total),
               x,
               y + 20
            );

            ctx.restore();
         },
      }),
      []
   );

   useEffect(() => {
      let isActive = true;
      const reqId = ++requestIdRef.current;

      const applyDashboardData = (data) => {
         const checkIn = getListLength(data.listUserCheckIn);
         const checkOut = getListLength(data.listUserCheckOut);
         const onLeave = getListLength(data.listUserLeave);

         const activeSegments = [
            checkIn,
            checkOut,
            onLeave,
         ].filter((item) => item > 0).length;

         const hasMultipleSegments = activeSegments > 1;

         setEmployeeData(data);
         setCheckInCount(checkIn);
         setCheckOutCount(checkOut);
         setOnLeaveCount(onLeave);

         setChartData({
            labels: [
               t('common.in'),
               t('common.out'),
               t('common.on-leave'),
            ],
            datasets: [
               {
                  data: [
                     checkIn,
                     checkOut,
                     onLeave,
                  ],
                  backgroundColor: [
                     '#009900',
                     '#1557F5',
                     '#FF006E',
                  ],
                  borderWidth: 0,
                  spacing: hasMultipleSegments ? 2 : 0,
                  borderRadius: hasMultipleSegments ? 8 : 0,
               },
            ],
         });
      };

      const fetchData = async () => {
         try {
            show();

            const response = await getEventDashboard(
               localStorage.getItem('i18nextLngId')
            );

            // Bỏ qua response cũ khi refresh/remount (mạng yếu dễ chồng request)
            if (!isActive || requestIdRef.current !== reqId) {
               return;
            }

            const payload = parseDashboardPayload(response?.data);
            const data = payload?.eventDashboard;

            const isSuccess =
               payload?.message === 'Successful' ||
               payload?.message === 'Thành công' ||
               data != null;

            if (isSuccess && data) {
               applyDashboardData(data);
            }
         } catch (error) {
            if (!isActive || requestIdRef.current !== reqId) {
               return;
            }

            if (
               error.response &&
               error.response.status !== 401
            ) {
               toast.error(t('common.content-030'));
            } else if (
               error.code === 'ECONNABORTED' ||
               !error.response
            ) {
               toast.error(t('common.network-timeout'));
            }
         } finally {
            // Luôn hide để cân bằng bộ đếm LoadingContext
            hide();
         }
      };

      fetchData();

      return () => {
         isActive = false;
      };
   }, [refreshData, show, hide, t]);

   const options = useMemo(
      () => ({
         responsive: true,
         maintainAspectRatio: false,

         cutout: '68%',

         onClick(event, elements) {
            if (!elements.length) return;

            const index = elements[0].index;

            let title = '';
            let employeeList = [];
            let statusType = 'CHECK_IN';

            if (index === 0) {
               title = t('common.content-031');
               employeeList =
                  employeeData.listUserCheckIn;
               statusType = 'CHECK_IN';
            } else if (index === 1) {
               title = t('common.content-032');
               employeeList =
                  employeeData.listUserCheckOut;
               statusType = 'CHECK_OUT';
            } else {
               title = t('common.content-033');
               employeeList =
                  employeeData.listUserLeave;
               statusType = 'LEAVE';
            }

            setDialogTitle(title);
            setDialogListEmployee(employeeList);
            setDialogStatusType?.(statusType);
            setOpenShowInfoDialog(true);
         },

         plugins: {
            employeesCenterText: {
               total: totalUser,
               title: t('common.total-user'),
            },
            tooltip: {
               enabled: true,
            },

            legend: {
               display: true,
               position: 'bottom',
               align: 'center',
               labels: {
                  usePointStyle: true,
                  pointStyle: 'circle',
                  boxHeight: 10,
                  boxWidth: 12,
                  padding: 12,
                  useBorderRadius: true,
                  borderRadius: 999,
                  color: '#262626',
                  font: {
                     size: 11.5,
                     family: "'Montserrat', sans-serif",
                     weight: '500',
                  },
                  maxWidth: 80,
               },
            },
         },
      }),
      [
         employeeData,
         totalUser,
         t,
         setDialogTitle,
         setDialogListEmployee,
         setDialogStatusType,
         setOpenShowInfoDialog,
      ]
   );

   return (
      <div className='card px-3 py-3 flex flex-col items-center h-full'>
         <div className="flex justify-center">
            <div className="w-[240px] h-[240px]">
               {totalUser > 0 ? (
                  <Doughnut
                     key={`employees-chart-${totalUser}-${checkInCount}-${checkOutCount}-${onLeaveCount}`}
                     data={chartData}
                     options={options}
                     plugins={[centerTextPlugin]}
                  />
               ) : (
                  <div className='h-full flex items-center justify-center'>
                     <p className='text-center text-gray-400'>
                        {t(
                           'common.content-160'
                        )}
                     </p>
                  </div>
               )}

            </div>
         </div>

         <ControlpanelSuccess3thButton
            onClick={() =>
               navigate(
                  '/controlpanel/user-management'
               )
            }
            className="
               mt-4
               w-full
               rounded-2xl
               border
               border-[#C7D2FE]
               bg-[#EEF2FF]
               text-[#1557F5]
               shadow-none
            "
         >
            <AddIcon />

            <p>{t('common.add-users')}</p>
         </ControlpanelSuccess3thButton>
      </div>
   );
};

export default EmployeesChart;